import { useState, useEffect } from 'react';
import { useFeedback } from '../common/Feedback';
import { Eye, EyeOff, Building, Copy, Check, Pencil, Trash2, Info } from 'lucide-react';
import { RowMenu } from '../common/RowMenu';
import { Credential } from '../../hooks/useCredentials';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../api/client';
import { CodigoDoServidor } from './CodigoDoServidor';
import { generateTotpCode, getTotpRemainingSeconds } from '../../utils/qr';
import { useCatalog } from '../../hooks/useCatalog';
import { courtLabel, subtituloSemRepetir } from '../../utils/catalog';
import { AvisoInstalarExtensao, BotaoAcessar, useAcessar } from './BotaoAcessar';

interface CredentialCardProps {
  credential: Credential;
  onEdit: (c: Credential) => void;
  onDelete: (id: string) => void;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  /** Pasta do titular, mostrada nos resultados da busca na visão Pastas (Etapa 10a). */
  folderLabel?: string;
}

export function CredentialCard({ credential, onEdit, onDelete, selectionMode = false, selected = false, onToggleSelect, folderLabel }: CredentialCardProps) {
  const { avisar } = useFeedback();
  const { membership, user } = useAuth();
  const isAdmin = membership?.role === 'admin' || membership?.role === 'owner';
  const isCreator = credential.created_by === user?.id;
  const canManage = isAdmin || isCreator;
  const { catalog } = useCatalog();
  // M2: o subtítulo só traz o que o título ainda não diz.
  const tribunalLabel = subtituloSemRepetir(credential.label || '', courtLabel(catalog, credential.catalog_key));
  // D23: "Acessar" (já logado pela extensão; sem ela, abre a página de login e mostra o aviso).
  const acessar = useAcessar(credential);
  const [revealed, setRevealed] = useState<{ login?: string; password?: string; totp?: string } | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // Live TOTP code generated CLIENT-SIDE — independent of server clock
  const [liveCode, setLiveCode] = useState('');
  const [liveTimeLeft, setLiveTimeLeft] = useState(30);

  useEffect(() => {
    const secret = credential.totp_secret;
    if (!secret) {
      setLiveCode('');
      return;
    }

    const update = () => {
      setLiveCode(generateTotpCode(secret));
      setLiveTimeLeft(getTotpRemainingSeconds(30));
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [credential.totp_secret]);

  const handleReveal = async () => {
    if (revealed) {
      setRevealed(null);
      return;
    }
    
    setIsRevealing(true);
    try {
      const data = await apiClient.get<any>(`/credentials/${credential.id}?reveal=true`);
      setRevealed({ login: data.login, password: data.password, totp: data.totp_secret });
    } catch (e) {
      console.error('Failed to reveal credential');
    } finally {
      setIsRevealing(false);
    }
  };

  const fallbackCopyToClipboard = (text: string, field: string) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (successful) {
        setCopied(field);
        setTimeout(() => setCopied(null), 2000);
      } else {
        avisar('Não foi possível copiar automaticamente. Por favor, copie manualmente.', 'erro');
      }
    } catch (err) {
      console.error('Fallback copy failed', err);
      avisar('Falha ao copiar credencial.', 'erro');
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopied(field);
          setTimeout(() => setCopied(null), 2000);
        })
        .catch(err => {
          console.error('Clipboard write failed, trying fallback', err);
          fallbackCopyToClipboard(text, field);
        });
    } else {
      fallbackCopyToClipboard(text, field);
    }
  };

  const handleCopy = async (field: 'login' | 'password' | 'totpCode') => {
    if (revealed) {
      if (field === 'login' && revealed.login) copyToClipboard(revealed.login, 'login');
      if (field === 'password' && revealed.password) copyToClipboard(revealed.password, 'password');
      if (field === 'totpCode' && liveCode) copyToClipboard(liveCode, 'totpCode');
      return;
    }
    
    try {
      const data = await apiClient.post<any>(`/credentials/${credential.id}/use`);
      if (field === 'login') copyToClipboard(data.login, 'login');
      if (field === 'password') copyToClipboard(data.password, 'password');
      if (field === 'totpCode') {
        const code = data.totp_code || '';
        copyToClipboard(code, 'totpCode');
      }
    } catch (e) {
      console.error('Failed to copy credential', e);
      avisar('Falha ao copiar credencial.', 'erro');
    }
  };

  const maskString = (_str: string) => '•'.repeat(8);

  return (
    <div className="glass animate-scale-in" style={{
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-6)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--space-4)',
      minWidth: 0,
      boxShadow: 'var(--shadow-md)',
      transition: 'transform var(--duration-base) var(--ease-out), border-color var(--duration-base)',
    }}
    onMouseOver={(e) => {
      e.currentTarget.style.borderColor = 'rgba(26, 154, 102, 0.3)';
    }}
    onMouseOut={(e) => {
      e.currentTarget.style.borderColor = 'var(--color-border-default)';
    }}
    >
      {/* D23: o cabeçalho tem só o título e os ícones de editar/excluir; título longo quebra linha. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flex: 1, minWidth: 0 }}>
          {selectionMode && canManage && (
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelect?.(credential.id)}
              style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: 'var(--color-primary-500, #10b981)', flexShrink: 0 }}
              title="Selecionar para atualização em massa"
            />
          )}
          <div style={{ padding: 'var(--space-2)', background: 'var(--color-bg-elevated)', borderRadius: 'var(--radius-md)', flexShrink: 0 }}>
            <Building size={20} color="var(--color-primary-400)" />
          </div>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            {/* M2: título com altura fixa de duas linhas (reticências), para Login e Senha ficarem alinhados na fileira. */}
            <h3 title={credential.label} className="titulo-duas-linhas" style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)' }}>{credential.label}</h3>
            {tribunalLabel && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', overflowWrap: 'anywhere' }}>{tribunalLabel}</span>}
            {folderLabel && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary-400)', overflowWrap: 'anywhere' }}>Pasta: {folderLabel}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexShrink: 0 }}>
          {/* M3: editar fica à mão; excluir vai para o menu ⋯ e pede confirmação com o nome. */}
          {canManage && (
            <>
              <button type="button" className="botao-icone" onClick={() => onEdit(credential)} title="Editar credencial" aria-label={`Editar ${credential.label}`}>
                <Pencil size={16} />
              </button>
              <RowMenu
                rotulo={`Mais ações de ${credential.label}`}
                itens={[{ rotulo: 'Excluir credencial', icone: <Trash2 size={16} />, aoClicar: () => onDelete(credential.id), perigo: true }]}
              />
            </>
        )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginTop: 'var(--space-2)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '100px minmax(0, 1fr)', alignItems: 'center' }}>
          <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>Login:</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <code style={{ background: 'var(--color-bg-root)', padding: '2px 6px', borderRadius: '4px', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
              {revealed?.login || credential.login_masked || maskString('user')}
            </code>
            <button 
              onClick={() => handleCopy('login')} 
              className="botao-icone botao-icone--compacto botao-icone--sem-borda"
              type="button"
              title="Copiar login"
            >
              {copied === 'login' ? <Check size={16} color="var(--color-primary-400)" /> : <Copy size={16} />}
            </button>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: '100px minmax(0, 1fr)', alignItems: 'center' }}>
          <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>Senha:</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <code style={{ background: 'var(--color-bg-root)', padding: '2px 6px', borderRadius: '4px', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
              {revealed?.password || credential.password_masked || maskString('pass')}
            </code>
            <button 
              onClick={() => handleCopy('password')} 
              className="botao-icone botao-icone--compacto botao-icone--sem-borda"
              type="button"
              title="Copiar senha"
            >
              {copied === 'password' ? <Check size={16} color="var(--color-primary-400)" /> : <Copy size={16} />}
            </button>
          </div>
        </div>

        {credential.totp_secret && liveCode && (
          <div style={{ display: 'grid', gridTemplateColumns: '100px minmax(0, 1fr)', alignItems: 'center' }}>
            {/* M10: o código fica à vista de propósito; o ícone explica por quê. */}
            <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              Código 2FA:
              <span title="O código 2FA aparece sem precisar revelar porque muda a cada 30 segundos e, sozinho, não dá acesso: ainda é preciso o login e a senha." aria-label="O código 2FA aparece sem precisar revelar porque muda a cada 30 segundos e, sozinho, não dá acesso: ainda é preciso o login e a senha." role="img" tabIndex={0} style={{ display: 'inline-flex', cursor: 'help', color: 'var(--color-text-muted)' }}>
                <Info size={13} />
              </span>
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <code style={{
                background: 'var(--color-bg-root)',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: 'var(--text-sm)',
                color: liveTimeLeft <= 5 ? 'var(--color-error, #ef4444)' : 'var(--color-primary-400)',
                fontWeight: 'bold',
                letterSpacing: '2px',
                fontFamily: 'monospace',
                transition: 'color 0.3s ease'
              }}>
                {liveCode.slice(0, 3)} {liveCode.slice(3)}
              </code>
              {/* Inline circular countdown — no API call needed */}
              <div style={{ position: 'relative', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="24" height="24" viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)' }}>
                  <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="4" />
                  <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none"
                    stroke={liveTimeLeft <= 5 ? 'var(--color-error, #ef4444)' : 'var(--color-primary-500, #10b981)'}
                    strokeWidth="4"
                    strokeDasharray={`${(liveTimeLeft / 30) * 100}, 100`}
                    style={{ transition: 'stroke-dasharray 1s linear, stroke 0.3s ease' }}
                  />
                </svg>
                <span style={{ position: 'absolute', fontSize: '9px', fontWeight: 'bold', color: 'var(--color-text-primary)' }}>{liveTimeLeft}</span>
              </div>
              <button 
                onClick={() => handleCopy('totpCode')} 
                className="botao-icone botao-icone--compacto botao-icone--sem-borda"
                type="button"
                title="Copiar código 2FA"
              >
                {copied === 'totpCode' ? <Check size={16} color="var(--color-primary-400)" /> : <Copy size={16} />}
              </button>
            </div>
          </div>
        )}

        {!credential.totp_secret && credential.has_totp && (
          <div style={{ display: 'grid', gridTemplateColumns: '100px minmax(0, 1fr)', alignItems: 'center' }}>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>Código 2FA:</span>
            <div>
              <CodigoDoServidor credentialId={credential.id} />
            </div>
          </div>
        )}

        {revealed?.totp && (
          <div style={{ display: 'grid', gridTemplateColumns: '100px minmax(0, 1fr)', alignItems: 'center' }}>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)' }}>Segredo TOTP:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <code style={{ background: 'var(--color-bg-root)', padding: '2px 6px', borderRadius: '4px', fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', flex: 1, wordBreak: 'break-all' }}>
                {revealed.totp}
              </code>
              <button 
                onClick={() => copyToClipboard(revealed.totp!, 'totp')} 
                className="botao-icone botao-icone--compacto botao-icone--sem-borda"
                type="button"
                title="Copiar segredo TOTP"
              >
                {copied === 'totp' ? <Check size={16} color="var(--color-primary-400)" /> : <Copy size={16} />}
              </button>
            </div>
          </div>
        )}

        {isAdmin && (
          <div style={{ marginTop: 'var(--space-2)', display: 'flex', justifyContent: 'flex-end' }}>
            <button 
              onClick={handleReveal} 
              disabled={isRevealing}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--space-2)', 
                background: 'transparent', 
                border: 'none', 
                color: 'var(--color-primary-400)', 
                cursor: 'pointer', 
                fontSize: 'var(--text-sm)',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = 'var(--color-primary-300, #4dd49a)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = 'var(--color-primary-400)';
                e.currentTarget.style.transform = 'none';
              }}
            >
              {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
              {isRevealing ? 'Aguarde...' : (revealed ? 'Ocultar credenciais' : 'Revelar credenciais')}
            </button>
          </div>
        )}
      </div>

      {/* D23: "Acessar" é o botão de maior destaque do cartão (largura total, como o "Assinar PDF"). */}
      {acessar.ativo && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'auto' }}>
          <BotaoAcessar credentialId={credential.id} acao={acessar.acao} />
          {acessar.mostrarAviso && <AvisoInstalarExtensao centralizado />}
        </div>
      )}

    </div>
  );
}
